import { useMemo } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CircleAlert, User, UserPlus, UserRoundCheck } from "lucide-react";

import { buttonVariants } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import ACL_RELATIONSHIPS from "acl-relationships";
import SidebarDetailLayout from "components/SidebarDetailLayout";
import useAuthorized from "hooks/useAuthorized";
import usePageTitle from "hooks/usePageTitle";
import MetaAdAccountService from "services/MetaAdAccount";
import MetaLeadService from "services/MetaLead";
import LeadContactForm from "routes/meta_leads/LeadContactForm";
import LeadOverview from "routes/meta_leads/LeadOverview";

const LeadBreadcrumb = ({ account, lead, accountId }) => (
  <Breadcrumb>
    <BreadcrumbList className="tw:flex-nowrap">
      <BreadcrumbItem className="tw:min-w-0">
        <BreadcrumbLink
          className="tw:text-xs! tw:text-muted-foreground! tw:hover:text-foreground! tw:block tw:truncate"
          render={<Link to={`/leads/meta/${accountId}/leads`} />}
        >
          {account?.name ?? "Leads"}
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="tw:min-w-0">
        <BreadcrumbPage className="tw:text-xs! tw:text-foreground tw:font-semibold tw:block tw:truncate">
          {lead?.name ?? `Lead #${lead?.id ?? ""}`}
        </BreadcrumbPage>
      </BreadcrumbItem>
    </BreadcrumbList>
  </Breadcrumb>
);

const LeadDetailPage = () => {
  const { accountId, leadId } = useParams();
  const canRead = useAuthorized([ACL_RELATIONSHIPS.metaAdAccount.readLeads]);

  const {
    data: lead,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["meta-lead", accountId, leadId],
    queryFn: () => MetaLeadService.getLead(accountId, leadId),
    enabled: canRead,
  });

  // Reuse the account query the leads table already primed for the breadcrumb.
  const { data: account } = useQuery({
    queryKey: ["meta-ad-account", accountId],
    queryFn: () => MetaAdAccountService.get(accountId),
    enabled: canRead,
  });

  usePageTitle(lead?.name || `Lead #${leadId}`);

  const basePath = `/leads/meta/${accountId}/lead/${leadId}`;

  const sections = useMemo(() => {
    const items = [
      {
        key: "overview",
        label: "Overview",
        icon: User,
        content: <LeadOverview lead={lead} isLoading={isLoading} />,
      },
    ];
    // Reached only from the "Create Contact" action, not the rail. Once the lead
    // has a contact, this route sends the user to that contact instead.
    items.push({
      key: "contact",
      label: "Contact",
      hideFromSidebar: true,
      content: lead?.contact_id ? (
        <Navigate to={`/contacts/${lead.contact_id}`} replace />
      ) : (
        <div className="tw:w-full tw:max-w-7xl">
          <LeadContactForm lead={lead} leadId={leadId} onSaved={refetch} />
        </div>
      ),
    });
    return items;
  }, [lead, isLoading, refetch, leadId]);

  if (!canRead) {
    return (
      <div className="tw:w-full">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <CircleAlert />
            </EmptyMedia>
            <EmptyTitle>Access denied</EmptyTitle>
            <EmptyDescription>
              You don&apos;t have permission to view this lead.
            </EmptyDescription>
          </EmptyHeader>
          <Link to="/">Back to home</Link>
        </Empty>
      </div>
    );
  }

  return (
    <SidebarDetailLayout
      sections={sections}
      basePath={`/leads/meta/${accountId}/lead/${leadId}`}
      defaultSection="overview"
      header="Lead Detail"
      breadcrumb={
        <LeadBreadcrumb account={account} lead={lead} accountId={accountId} />
      }
      title={lead?.name || `Lead #${leadId}`}
      titleActions={
        lead?.contact_id ? (
          <Link
            to={`/contacts/${lead.contact_id}`}
            className={cn(
              buttonVariants({ variant: "outline", size: "sm" }),
              "tw:text-foreground!"
            )}
          >
            <UserRoundCheck data-icon="inline-start" />
            View Contact
          </Link>
        ) : lead ? (
          <Link
            to={`${basePath}/contact`}
            className={cn(
              buttonVariants({ variant: "default", size: "sm" }),
              "tw:bg-primary! tw:text-primary-foreground!"
            )}
          >
            <UserPlus data-icon="inline-start" />
            Create Contact
          </Link>
        ) : null
      }
    />
  );
};

export default LeadDetailPage;
